"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Separator } from "../ui/separator";
import { Badge } from "../ui/badge";
import { Save, Calculator, Loader2, MessageSquare } from "lucide-react";
import { DocumentManager } from "./DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { ReviewComment } from "../shared/ReviewComment";

const BENCHMARK_OPTIONS = [
  "Cash Flow from Operations",
  "EBITDA",
  "Total Expenses",
  "Revenue from Operations",
  "Total Assets",
  "Total equity/net assets",
  "Profit Before Tax"
];

const PERFORMANCE_FACTORS = [
  {
    label: "Reliability on internal controls",
    options: ["Effective", "Not Effective"],
  },
  { label: "Engagement risk", options: ["Increased", "Not Increased"] },
  {
    label: "Forecasting changes",
    options: [
      "No changes",
      "Significant and could impact expected misstatements",
    ],
  },
  {
    label: "History of misstatements",
    options: ["Limited to no errors", "High value or amount"],
  },
  {
    label: "Management responsiveness",
    options: [
      "Investigates and corrects",
      "Does not investigate and/or correct",
    ],
  },
];

interface MaterialityPlanningProps {
  onDataChange: (data: any) => void;
  initialData: any;
  crossSectionAnswers?: Record<string, string | number | boolean>;
  companyName?: string;
}

export function MaterialityPlanning({
  onDataChange,
  initialData,
  crossSectionAnswers = {},
  companyName: propCompanyName,
}: MaterialityPlanningProps) {
  const { isArchivalMode } = useArchivalMode();
  const [companyName, setCompanyName] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reviewCommentOpen, setReviewCommentOpen] = useState(false);
  
  // Document states for M-1, M-4, M-6
  const [m1Documents, setM1Documents] = useState<any[]>([]);
  const [m1TextResponse, setM1TextResponse] = useState<any>(null);
  const [m4Documents, setM4Documents] = useState<any[]>([]);
  const [m4TextResponse, setM4TextResponse] = useState<any>(null);
  const [m6Documents, setM6Documents] = useState<any[]>([]);
  const [m6TextResponse, setM6TextResponse] = useState<any>(null);

  const [formData, setFormData] = useState({
    commonUsers: "",
    selectedBenchmarks: [] as string[],
    benchmarkData: [] as Array<{
      benchmark: string;
      amount: number;
      adjustments: number;
      factor: number;
    }>,
    determinedMateriality: 0,
    calculatedPerformanceMateriality: 0,
    benchmarkFactors: "",
    performancePercentage: 75,
    performanceFactors: {} as Record<string, string>,
    performanceDocumentation: "",
    trivialPercentage: 5,
    trivialThreshold: 0,
  });

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      if (propCompanyName) {
        setCompanyName(propCompanyName);
        return;
      }
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, [propCompanyName]);

  // Load data from SharePoint when component mounts
  useEffect(() => {
    const loadFromSharePoint = async () => {
      if (!companyName) return;

      try {
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI?.loadResponses) {
          console.log("Planning API not available, using initialData");
          if (initialData) {
            setFormData(initialData);
          }
          return;
        }

        const sharePointResponses = await planningAPI.loadResponses({
          companyName,
          sectionKey: "materiality"
        });

        if (sharePointResponses && sharePointResponses.success && sharePointResponses.data) {
          const responses = sharePointResponses.data.responses || sharePointResponses.data;
          console.log("Loaded Materiality responses from SharePoint:", responses);

          // Start with initialData or empty form
          const loadedData = initialData ? { ...initialData } : {
            commonUsers: "",
            selectedBenchmarks: [] as string[],
            benchmarkData: [] as Array<{
              benchmark: string;
              amount: number;
              adjustments: number;
              factor: number;
            }>,
            determinedMateriality: 0,
            calculatedPerformanceMateriality: 0,
            benchmarkFactors: "",
            performancePercentage: 75,
            performanceFactors: {} as Record<string, string>,
            performanceDocumentation: "",
            trivialPercentage: 5,
            trivialThreshold: 0,
          };

          // Load M-1 (Common Users Documentation)
          if (responses['M-1']) {
            const m1Response = responses['M-1'];
            if (m1Response.textResponse) {
              setM1TextResponse(m1Response.textResponse);
            }
            if (m1Response.documents) {
              setM1Documents(m1Response.documents);
            }
          }

          // Load M-2 (Benchmark Selection with table)
          if (responses['M-2']) {
            const m2Response = responses['M-2'];
            if (m2Response.selectedOption && Array.isArray(m2Response.selectedOption)) {
              loadedData.selectedBenchmarks = m2Response.selectedOption;
            }
            if (m2Response.table && m2Response.table.rows) {
              // Convert table format to benchmarkData format
              // table.columns contains field definitions (headers)
              // table.rows contains the actual data rows
              loadedData.benchmarkData = m2Response.table.rows.map((row: any) => {
                return {
                  benchmark: row.benchmark || "",
                  amount: row.amount || 0,
                  adjustments: row.normalizationAdjustments || 0,
                  factor: row.benchmarkFactor || 5
                };
              });
            }
          }

          // Load M-3 (Determined Materiality)
          if (responses['M-3'] && responses['M-3'].textResponse) {
            const materiality = parseFloat(responses['M-3'].textResponse.plainText || responses['M-3'].textResponse.html || '0');
            loadedData.determinedMateriality = materiality || 0;
          }

          // Load M-4 (Benchmark Factors Documentation)
          if (responses['M-4']) {
            const m4Response = responses['M-4'];
            if (m4Response.textResponse) {
              setM4TextResponse(m4Response.textResponse);
              loadedData.benchmarkFactors = m4Response.textResponse.plainText || m4Response.textResponse.html || '';
            }
            if (m4Response.documents) {
              setM4Documents(m4Response.documents);
            }
          }

          // Load M-5 (Performance Materiality Percentage)
          if (responses['M-5'] && responses['M-5'].textResponse) {
            const percentage = parseFloat(responses['M-5'].textResponse.plainText || responses['M-5'].textResponse.html || '75');
            loadedData.performancePercentage = percentage || 75;
            // Check for override
            if (responses['M-5'].overridePerformanceMateriality) {
              loadedData.calculatedPerformanceMateriality = responses['M-5'].overridePerformanceMateriality;
            }
          }

          // Load M-6 (Performance Materiality Factors)
          if (responses['M-6']) {
            const m6Response = responses['M-6'];
            if (m6Response.selectedOption && typeof m6Response.selectedOption === 'object') {
              loadedData.performanceFactors = m6Response.selectedOption;
            }
            if (m6Response.textResponse) {
              setM6TextResponse(m6Response.textResponse);
              loadedData.performanceDocumentation = m6Response.textResponse.plainText || m6Response.textResponse.html || '';
            }
            if (m6Response.documents) {
              setM6Documents(m6Response.documents);
            }
          }

          // Load M-7 (Trivial Percentage)
          if (responses['M-7'] && responses['M-7'].textResponse) {
            const percentage = parseFloat(responses['M-7'].textResponse.plainText || responses['M-7'].textResponse.html || '5');
            loadedData.trivialPercentage = percentage || 5;
          }

          // Load M-8 (Trivial Threshold)
          if (responses['M-8'] && responses['M-8'].textResponse) {
            const threshold = parseFloat(responses['M-8'].textResponse.plainText || responses['M-8'].textResponse.html || '0');
            loadedData.trivialThreshold = threshold || 0;
          }

          setFormData(loadedData);
        } else {
          // Fallback to initialData if SharePoint loading fails
          if (initialData) {
            setFormData(initialData);
          }
        }
      } catch (error) {
        console.error("Failed to load Materiality responses from SharePoint:", error);
        // Fallback to initialData
        if (initialData) {
          setFormData(initialData);
        }
      }
    };

    loadFromSharePoint();
  }, [companyName, initialData]);

  useEffect(() => {
    // Only auto-calculate if the fields are empty (0) - allows manual override
    const autoThreshold =
      (formData.determinedMateriality * formData.trivialPercentage) / 100;
    const autoPerformanceMateriality =
      (formData.determinedMateriality * formData.performancePercentage) / 100;

    setFormData((prev) => ({
      ...prev,
      // Only update if current value is 0 (not manually set)
      trivialThreshold:
        prev.trivialThreshold === 0 ? autoThreshold : prev.trivialThreshold,
      calculatedPerformanceMateriality:
        prev.calculatedPerformanceMateriality === 0
          ? autoPerformanceMateriality
          : prev.calculatedPerformanceMateriality,
    }));
  }, [
    formData.determinedMateriality,
    formData.trivialPercentage,
    formData.performancePercentage,
  ]);

  const handleBenchmarkSelection = (benchmark: string, checked: boolean) => {
    setFormData((prev) => {
      const newSelected = checked
        ? [...prev.selectedBenchmarks, benchmark]
        : prev.selectedBenchmarks.filter((b) => b !== benchmark);

      const newBenchmarkData = newSelected.map((b) => {
        const existing = prev.benchmarkData.find((bd) => bd.benchmark === b);
        return (
          existing || { benchmark: b, amount: 0, adjustments: 0, factor: 5 }
        );
      });

      return {
        ...prev,
        selectedBenchmarks: newSelected,
        benchmarkData: newBenchmarkData,
      };
    });
  };

  const updateBenchmarkData = (index: number, field: string, value: number) => {
    setFormData((prev) => {
      const newData = [...prev.benchmarkData];
      newData[index] = { ...newData[index], [field]: value };
      return { ...prev, benchmarkData: newData };
    });
  };

  const calculateMateriality = () => {
    // Calculate materiality for each benchmark and take the minimum
    const materialities = formData.benchmarkData.map((item) => {
      const normalizedAmount = item.amount + item.adjustments;
      return (normalizedAmount * item.factor) / 100;
    });

    // Find the minimum materiality value (only if there are benchmarks with positive values)
    const positiveMaterialities = materialities.filter(m => m > 0);
    const minimum = positiveMaterialities.length > 0 
      ? Math.min(...positiveMaterialities)
      : 0;

    setFormData((prev) => ({ ...prev, determinedMateriality: minimum }));
  };

  const handlePerformanceFactorChange = (factor: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      performanceFactors: { ...prev.performanceFactors, [factor]: value },
    }));
  };

  const handleSave = async () => {
    // Call the original onDataChange for local state management
    onDataChange(formData);
    
    // Save to SharePoint
    if (!companyName) {
      setSaveError("Company name not available");
      setSaveStatus('error');
      return;
    }

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const responses: Record<string, any> = {};

      // M-1: Common Users Documentation (document type)
      const hasM1Documents = m1Documents && Array.isArray(m1Documents) && m1Documents.length > 0;
      const hasM1TextResponse = m1TextResponse && (
        (m1TextResponse.html && m1TextResponse.html.trim().length > 0) || 
        (m1TextResponse.plainText && m1TextResponse.plainText.trim().length > 0)
      );
      
      // Always save if we have ANY data (documents or textResponse)
      // This ensures M-1 is saved even with minimal content
      if (hasM1Documents || hasM1TextResponse) {
        // Build documents array (excluding textbox type)
        const fileDocuments = hasM1Documents 
          ? m1Documents.filter((doc: any) => doc.type !== 'textbox').map((doc: any) => ({
              name: doc.name,
              type: doc.type,
              url: doc.url,
              reference: doc.reference
            }))
          : [];
        
        // Build textResponse - only from state (from SharePoint)
        const m1TextResponseData = hasM1TextResponse ? m1TextResponse : null;
        
        responses['M-1'] = {
          questionId: 'M-1',
          selectedOption: null,
          textResponse: m1TextResponseData,
          documents: fileDocuments,
          lastUpdated: new Date().toISOString(),
          companyName
        };
        
        console.log('[M-1 SAVE] Saving M-1 response:', {
          hasDocuments: hasM1Documents,
          documentCount: fileDocuments.length,
          hasTextResponse: hasM1TextResponse
        });
      } else {
        console.log('[M-1 SAVE] Skipping M-1 - no data found');
      }

      // M-2: Benchmark Selection (multi-select with table)
      if (formData.selectedBenchmarks.length > 0) {
        // Save selected options as a list
        responses['M-2'] = {
          questionId: 'M-2',
          selectedOption: formData.selectedBenchmarks, // Array of selected benchmarks
          textResponse: null,
          documents: [],
          // Table structure
          table: {
            columns: [
              { key: "benchmark", label: "Benchmark" },
              { key: "amount", label: "Amount (A)" },
              { key: "normalizationAdjustments", label: "Normalization Adjustments (B)" },
              { key: "normalizedAmount", label: "Normalized Amount (C=A+B)" },
              { key: "benchmarkFactor", label: "Benchmark Factor (%)" },
              { key: "materiality", label: "Materiality (C×%)" }
            ],
            rows: formData.benchmarkData.map(item => {
              const normalizedAmount = item.amount + item.adjustments;
              const materiality = (normalizedAmount * item.factor) / 100;
              return {
                benchmark: item.benchmark,
                amount: item.amount,
                normalizationAdjustments: item.adjustments,
                normalizedAmount: normalizedAmount,
                benchmarkFactor: item.factor,
                materiality: materiality
              };
            })
          },
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // M-3: Determined Materiality (text type)
      if (formData.determinedMateriality > 0) {
        responses['M-3'] = {
          questionId: 'M-3',
          selectedOption: null,
          textResponse: {
            html: String(formData.determinedMateriality),
            plainText: String(formData.determinedMateriality)
          },
          documents: [],
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // M-4: Benchmark Factors Documentation (document type)
      if (m4Documents.length > 0 || m4TextResponse || formData.benchmarkFactors) {
        responses['M-4'] = {
          questionId: 'M-4',
          selectedOption: null,
          textResponse: m4TextResponse || (formData.benchmarkFactors ? {
            html: formData.benchmarkFactors,
            plainText: formData.benchmarkFactors
          } : null),
          documents: m4Documents.filter(doc => doc.type !== 'textbox').map((doc: any) => ({
            name: doc.name,
            type: doc.type,
            url: doc.url,
            reference: doc.reference
          })),
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // M-5: Performance Materiality Percentage (text type)
      if (formData.performancePercentage > 0) {
        responses['M-5'] = {
          questionId: 'M-5',
          selectedOption: null,
          textResponse: {
            html: String(formData.performancePercentage),
            plainText: String(formData.performancePercentage)
          },
          documents: [],
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // M-6: Performance Materiality Factors (document type with subInputs)
      const hasPerformanceFactors = Object.keys(formData.performanceFactors).length > 0;
      if (m6Documents.length > 0 || m6TextResponse || hasPerformanceFactors || formData.performanceDocumentation) {
        // Build selectedOption as an object with each factor's selection
        const performanceFactorsOption: Record<string, string> = {};
        Object.keys(formData.performanceFactors).forEach(key => {
          if (formData.performanceFactors[key]) {
            performanceFactorsOption[key] = formData.performanceFactors[key];
          }
        });

        responses['M-6'] = {
          questionId: 'M-6',
          selectedOption: hasPerformanceFactors ? performanceFactorsOption : null,
          textResponse: m6TextResponse || (formData.performanceDocumentation ? {
            html: formData.performanceDocumentation,
            plainText: formData.performanceDocumentation
          } : null),
          documents: m6Documents.filter(doc => doc.type !== 'textbox').map((doc: any) => ({
            name: doc.name,
            type: doc.type,
            url: doc.url,
            reference: doc.reference
          })),
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // M-7: Clearly Trivial Threshold Percentage (percent type)
      if (formData.trivialPercentage > 0) {
        responses['M-7'] = {
          questionId: 'M-7',
          selectedOption: null,
          textResponse: {
            html: String(formData.trivialPercentage),
            plainText: String(formData.trivialPercentage)
          },
          documents: [],
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // M-8: Clearly Trivial Threshold Computed Value (text type) - also save override
      if (formData.trivialThreshold > 0) {
        responses['M-8'] = {
          questionId: 'M-8',
          selectedOption: null,
          textResponse: {
            html: String(formData.trivialThreshold),
            plainText: String(formData.trivialThreshold)
          },
          documents: [],
          // Store override value separately if different from calculated
          overrideValue: formData.trivialThreshold,
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // Also save override for performance materiality if different from calculated
      if (formData.calculatedPerformanceMateriality > 0) {
        const calculatedValue = (formData.determinedMateriality * formData.performancePercentage) / 100;
        if (Math.abs(formData.calculatedPerformanceMateriality - calculatedValue) > 0.01) {
          // There's an override
          if (!responses['M-5']) {
            responses['M-5'] = {
              questionId: 'M-5',
              selectedOption: null,
              textResponse: {
                html: String(formData.performancePercentage),
                plainText: String(formData.performancePercentage)
              },
              documents: [],
              lastUpdated: new Date().toISOString(),
              companyName
            };
          }
          responses['M-5'].overridePerformanceMateriality = formData.calculatedPerformanceMateriality;
        }
      }

      // Save all responses to SharePoint
      if (Object.keys(responses).length > 0) {
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI) {
          throw new Error("Planning API not available");
        }

        const result = await planningAPI.saveAllResponses({
          companyName,
          sectionKey: "materiality",
          responses
        });

        if (result.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          throw new Error(result.error || "Failed to save responses");
        }
      } else {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (error) {
      console.error("Failed to save materiality responses:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  // Debug log for tracking cross-section answers (for future conditional logic)
  console.log("MaterialityPlanning crossSectionAnswers:", crossSectionAnswers);

  return (
    <div className="space-y-8">
      {/* Header with Review Comment Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Materiality Planning</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setReviewCommentOpen(true)}
          className="text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
          title="Review Comment"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Review Comment
        </Button>
      </div>
      
      {/* Common Users Documentation - M-1 */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Common Users & Factors</h3>
        <div className="space-y-2">
          <Label>
            Document the common users of the financial statements and other
            factors affecting materiality determination (for eg. misstatements identified in the previous audit, changes in surrounding circumstances or significant change in common users as a group).
          </Label>
          <DocumentManager
            sectionKey="materiality"
            groupKey="Group materiality at planning stage"
            itemId="M-1"
            itemQuestion="Document the common users of the financial statements of the entity and other factors affecting determination of materiality (for eg. misstatements identified in the previous audit, changes in surrounding circumstances or significant change in common users as a group)."
            itemTextResponse={m1TextResponse}
            itemDocuments={m1Documents}
            onDocumentsChange={setM1Documents}
            onTextResponseChange={setM1TextResponse}
          />
        </div>
      </div>

      <Separator />

      {/* Benchmark Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Benchmark Selection</h3>
        <div className="grid grid-cols-2 gap-4">
          {BENCHMARK_OPTIONS.map((benchmark) => (
            <div key={benchmark} className="flex items-center space-x-2">
              <Checkbox
                id={benchmark}
                checked={formData.selectedBenchmarks.includes(benchmark)}
                onCheckedChange={(checked) =>
                  handleBenchmarkSelection(benchmark, checked as boolean)
                }
              />
              <Label htmlFor={benchmark} className="text-sm">
                {benchmark}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Benchmark Data Table */}
      {formData.selectedBenchmarks.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Benchmark Calculations</h3>
            <Button
              onClick={calculateMateriality}
              className="flex items-center gap-2"
            >
              <Calculator className="h-4 w-4" />
              Calculate Materiality
            </Button>
          </div>

          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Benchmark</TableHead>
                  <TableHead>Amount (A)</TableHead>
                  <TableHead>Normalization Adjustments (B)</TableHead>
                  <TableHead>Normalized Amount (C=A+B)</TableHead>
                  <TableHead>Benchmark Factor (%)</TableHead>
                  <TableHead>Materiality (C×%)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formData.benchmarkData.map((item, index) => {
                  const normalizedAmount = item.amount + item.adjustments;
                  const materiality = (normalizedAmount * item.factor) / 100;

                  return (
                    <TableRow key={item.benchmark}>
                      <TableCell className="font-medium">
                        {item.benchmark}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.amount || ""}
                          onChange={(e) =>
                            updateBenchmarkData(
                              index,
                              "amount",
                              Number.parseFloat(e.target.value) || 0,
                            )
                          }
                          className="w-24"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.adjustments || ""}
                          onChange={(e) =>
                            updateBenchmarkData(
                              index,
                              "adjustments",
                              Number.parseFloat(e.target.value) || 0,
                            )
                          }
                          className="w-24"
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {normalizedAmount.toLocaleString('en-IN')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.factor || ""}
                          onChange={(e) =>
                            updateBenchmarkData(
                              index,
                              "factor",
                              Number.parseFloat(e.target.value) || 0,
                            )
                          }
                          className="w-20"
                          min="0"
                          max="100"
                          step="0.1"
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {materiality.toLocaleString('en-IN')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="bg-primary/5 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold">
                Total Determined Materiality:
              </span>
              <div className="flex items-center gap-4">
                <Badge variant="outline" className="px-4 py-2 text-lg">
                  Calculated:{" "}
                  {formData.benchmarkData.length > 0
                    ? (() => {
                        const materialities = formData.benchmarkData.map((item) => {
                          const normalizedAmount = item.amount + item.adjustments;
                          return (normalizedAmount * item.factor) / 100;
                        });
                        const positiveMaterialities = materialities.filter(m => m > 0);
                        const minimum = positiveMaterialities.length > 0 
                          ? Math.min(...positiveMaterialities)
                          : 0;
                        return minimum.toLocaleString('en-IN');
                      })()
                    : '0'}
                </Badge>
                <div className="flex items-center gap-2">
                  <Label htmlFor="determinedMateriality" className="text-sm">
                    Final:
                  </Label>
                  <Input
                    id="determinedMateriality"
                    type="number"
                    value={formData.determinedMateriality || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        determinedMateriality:
                          Number.parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="w-32"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Separator />

      {/* Benchmark Factors Documentation - M-4 */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          Benchmark Factors Documentation
        </h3>
        <div className="space-y-2">
          <Label>
            Document the factors used to consider benchmarks for materiality:
            <ul className="list-disc list-inside mt-2 text-sm text-muted-foreground">
              <li>The entity's ownership structure, the way it is financed and relative volatility of the benchmark;</li>
              <li>The elements of the financial statements;</li>
              <li>Items on which attention of users tends to be focused;</li>
              <li>Nature of the entity, where it is in its life cycle, and the industry and economic environment in which it operates;</li>
            </ul>
          </Label>
          <DocumentManager
            sectionKey="materiality"
            groupKey="Group materiality at planning stage"
            itemId="M-4"
            itemQuestion="Document the factors used to consider benchmarks for materiality:\n- The entity's ownership structure, the way it is financed and relative volatility of the benchmark;\n- The elements of the financial statements;\n- Items on which attention of users tends to be focused;\n- Nature of the entity, where it is in its life cycle, and the industry and economic environment in which it operates;"
            itemTextResponse={m4TextResponse}
            itemDocuments={m4Documents}
            onDocumentsChange={setM4Documents}
            onTextResponseChange={setM4TextResponse}
          />
        </div>
      </div>

      <Separator />

      {/* Performance Materiality */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Performance Materiality</h3>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="performancePercentage">
              Performance Materiality Percentage
            </Label>
            <Input
              id="performancePercentage"
              type="number"
              value={formData.performancePercentage}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  performancePercentage: Number.parseFloat(e.target.value) || 0,
                }))
              }
              min="1"
              max="99"
              className="w-24"
            />
            <p className="text-muted-foreground text-sm">
              Must be less than 100%
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="calculatedPerformanceMateriality">
              Calculated Performance Materiality
            </Label>
            <div className="flex items-center gap-4">
              <div className="bg-secondary rounded-md p-3">
                <Badge variant="secondary" className="text-base">
                  Auto:{" "}
                  {(
                    (formData.determinedMateriality *
                      formData.performancePercentage) /
                    100
                  ).toLocaleString('en-IN')}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="calculatedPerformanceMateriality"
                  className="text-sm"
                >
                  Override:
                </Label>
                <Input
                  id="calculatedPerformanceMateriality"
                  type="number"
                  value={formData.calculatedPerformanceMateriality || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      calculatedPerformanceMateriality:
                        Number.parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-32"
                  placeholder="Optional override"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium">Performance Materiality Factors</h4>
          <div className="space-y-4">
            {PERFORMANCE_FACTORS.map((factor) => (
              <div key={factor.label} className="space-y-2">
                <Label className="text-sm font-medium">{factor.label}</Label>
                <RadioGroup
                  value={formData.performanceFactors[factor.label] || ""}
                  onValueChange={(value) =>
                    handlePerformanceFactorChange(factor.label, value)
                  }
                  className="flex gap-6"
                >
                  {factor.options.map((option) => (
                    <div key={option} className="flex items-center space-x-2">
                      <RadioGroupItem
                        value={option}
                        id={`${factor.label}-${option}`}
                      />
                      <Label
                        htmlFor={`${factor.label}-${option}`}
                        className="text-sm"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>
            Document factors used for consideration of performance materiality
          </Label>
          <DocumentManager
            sectionKey="materiality"
            groupKey="Group materiality at planning stage"
            itemId="M-6"
            itemQuestion="Document factors used for consideration of performance materiality."
            itemTextResponse={m6TextResponse}
            itemDocuments={m6Documents}
            onDocumentsChange={setM6Documents}
            onTextResponseChange={setM6TextResponse}
          />
        </div>
      </div>

      <Separator />

      {/* Clearly Trivial Threshold */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Clearly Trivial Threshold</h3>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="trivialPercentage">
              Clearly Trivial Threshold Percentage
            </Label>
            <Input
              id="trivialPercentage"
              type="number"
              value={formData.trivialPercentage}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  trivialPercentage: Number.parseFloat(e.target.value) || 0,
                }))
              }
              min="1"
              max="10"
              step="0.1"
              className="w-24"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="trivialThreshold">
              Computed Clearly Trivial Threshold
            </Label>
            <div className="flex items-center gap-4">
              <div className="bg-secondary rounded-md p-3">
                <Badge variant="secondary" className="text-base">
                  Auto:{" "}
                  {(
                    (formData.determinedMateriality *
                      formData.trivialPercentage) /
                    100
                  ).toLocaleString('en-IN')}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="trivialThreshold" className="text-sm">
                  Override:
                </Label>
                <Input
                  id="trivialThreshold"
                  type="number"
                  value={formData.trivialThreshold || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      trivialThreshold: Number.parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-32"
                  placeholder="Optional override"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center gap-4 pt-6">
        {saveStatus === 'saved' && (
          <Badge variant="outline" className="border-green-600 text-green-600">
            Saved successfully
          </Badge>
        )}
        {saveStatus === 'error' && saveError && (
          <Badge variant="destructive" className="text-red-600">
            Error: {saveError}
          </Badge>
        )}
        <Button 
          onClick={handleSave} 
          className="flex items-center gap-2"
          disabled={isArchivalMode || saving || !companyName}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Planning Data
            </>
          )}
        </Button>
      </div>
      
      {/* Review Comment Modal */}
      <ReviewComment
        questionId="Materiality-Planning"
        questionText="Materiality Planning"
        isOpen={reviewCommentOpen}
        onClose={() => setReviewCommentOpen(false)}
        workspace="Materiality"
        subPhase="Materiality Planning"
      />
    </div>
  );
}
